import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';

import css from './SearchResultsPanel.module.css';
import { ListingCardAnimate } from '../../../components/GsapAnimation/ListingCardAnimate/ListingCardAnimate';
import { delay } from 'lodash';
import { MIXPANEL_EVENTS, trackMixpanelEvent } from '../../../util/mixpanel';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    intl,
    goldenListing,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  const [isMobile, setIsMobile] = useState(false);
  const lastTrackedResults = useRef(null);

  useEffect(() => {
    const resultsSignature = listings.map(listing => listing?.id?.uuid).filter(Boolean).join(',');
    if (lastTrackedResults.current !== resultsSignature) {
      lastTrackedResults.current = resultsSignature;
      trackMixpanelEvent(MIXPANEL_EVENTS.SEARCH_RESULTS_VIEWED, {
        result_count: listings.length,
        page: pagination?.page || 1,
      });
    }
  }, [listings, pagination]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={classes}>
      <ul className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {listings.map((l, index) => (
          <ListingCardAnimate
            key={l.id.uuid}
            options={{ index, y: 60, opacity: 0.01, duration: isMobile ? .4 : 0.8, stagger: 0.2, once: false, markers: false, scrub: false, delay: isMobile ? .1 : 0.1, start: 'top end', end: "top center" }}
          >
            <li key={l.id.uuid} className={css.resultItem}>
              <ListingCard
                className={css.listingCard}
                listing={l}
                renderSizes={cardRenderSizes(isMapVariant)}
                setActiveListing={setActiveListing}
                goldenListing={goldenListing}
                analyticsSource="search_results"
                analyticsPosition={index}
              />
            </li>
          </ListingCardAnimate>
        ))}
        {props.children}
      </ul>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
